import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
} from '@amzn/awsui-components-console';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import InputWithValidation from '../../../lib/InputWithValidation';
import { isValidInput, isValidEmailInput, getCurrentAuthUserEmail } from '../../../lib/utils';
import { displayErrorContent, isAlphanumericInput } from '../../../lib/utils';
import { onboardLab } from '../apis';
import { CLIENT_ERROR, SERVER_ERROR } from '../../../lib/constants';

export default function OnboardLab() {
    const history = useHistory();
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [labId, setLabId] = React.useState('');
    const [location, setLocation] = React.useState('');
    const [locationError, setLocationError] = React.useState('');
    const [teamName, setTeamName] = React.useState('');
    const [teamNameError, setTeamNameError] = React.useState('');
    const [owner, setOwner] = React.useState('');
    const [accessGroup, setAccessGroup] = React.useState('');
    const [accessGroupError, setAccessGroupError] = React.useState('');
    const [dotsPoolName, setDotsPoolName] = React.useState('');
    const [monitorEnabled, setMonitorEnabled] = React.useState(true);
    const [ticketCategory, setTicketCategory] = React.useState('');
    const [ticketCategoryError, setTicketCategoryError] = React.useState('');
    const [ticketType, setTicketType] = React.useState('');
    const [ticketTypeError, setTicketTypeError] = React.useState('');
    const [ticketItem, setTicketItem] = React.useState('');
    const [ticketItemError, setTicketItemError] = React.useState('');
    const [ticketGroup, setTicketGroup] = React.useState('');
    const [ticketGroupError, setTicketGroupError] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    /**
     * set initial status
     */
    const setInitialStatus = () => {
        setLoading(true);
        setErrorMessage([]);
    };

    /**
     * validate input
     */
    const validateInput = () => {
        let errorflag = false;
        if (!isValidInput(location)) {
            setLocationError('Lab location is required to generate lab id.');
            errorflag = true;
        } else if (!isAlphanumericInput(location)) {
            setLocationError('Location should only contain uppercase letters (A-Z) and numbers (0-9).');
            errorflag = true;
        }
        if (!isValidInput(teamName)) {
            setTeamNameError('Team name is required to generate lab id.');
            errorflag = true;
        } else if (!isAlphanumericInput(teamName)) {
            setTeamNameError('Team name should only contain uppercase letters (A-Z) and numbers (0-9).');
            errorflag = true;
        }
        if (!isValidInput(accessGroup)) {
            setAccessGroupError('Access group is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketCategory)) {
            setTicketCategoryError('Category for ticket is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketType)) {
            setTicketTypeError('Type for ticket is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketItem)) {
            setTicketItemError('Item for ticket is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketGroup)) {
            setTicketGroupError('Resolver group for ticket is required and should not be empty.');
            errorflag = true;
        }
        return !errorflag;
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        let success = false;
        setInitialStatus();
        try {
            if (!validateInput()) {
                success = false;
            } else {
                await onboardLab(
                    labId,
                    owner,
                    accessGroup,
                    ticketCategory,
                    ticketType,
                    ticketItem,
                    ticketGroup,
                    dotsPoolName,
                    monitorEnabled
                );
                success = true;
            }
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status === 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errorMessage = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMessage, setErrorMessage);
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to onboard this lab, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
        }
        setLoading(false);
        if (success) {
            history.push('/lab-management/labs');
        }
    };

    /**
     * handle team name change
     */
    const onTeamNameChange = (value) => {
        const upperCaseValue = value.toUpperCase();
        setTeamName(upperCaseValue);
        setLabId(`DDF-${location}-${upperCaseValue}`);
    };

    /**
     * handle location change
     */
    const onLocationChange = (value) => {
        const upperCaseValue = value.toUpperCase();
        setLocation(upperCaseValue);
        setLabId(`DDF-${upperCaseValue}-${teamName}`);
    };

    useEffect(() => {
        // initialize the component
        async function setDefaultOwner() {
            const email = await getCurrentAuthUserEmail();
            setOwner(email);
        }
        setDefaultOwner();
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Lab management', href: '#/lab-management/labs' },
                    { text: 'Onboard', href: '#/lab-management/labs/onboard' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" href="#/lab-management/labs">
                                Back
                            </Button>
                            <Button formAction="none" loading={loading} onClick={onClick} variant="primary">
                                Confirm
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Container header={<Header variant="h2">Onboard lab</Header>}>
                            <SpaceBetween direction="vertical" size="m">
                                <SpaceBetween direction="horizontal" size="m">
                                    <InputWithValidation
                                        label="Lab location"
                                        type="text"
                                        description="Provide lab location to auto populate a lab ID"
                                        errorText={locationError}
                                        placeholder="Enter lab location e.g. SEA41"
                                        value={location}
                                        setValue={onLocationChange}
                                        setError={setLocationError}
                                        constraintText="Valid characters include A-Z and 0-9"
                                    ></InputWithValidation>
                                    <InputWithValidation
                                        label="Team name"
                                        type="text"
                                        description="Provide team name to auto populate a lab ID"
                                        errorText={teamNameError}
                                        placeholder="Enter team name"
                                        value={teamName}
                                        setValue={onTeamNameChange}
                                        setError={setTeamNameError}
                                        constraintText="Valid characters include A-Z and 0-9"
                                    ></InputWithValidation>
                                    <FormField
                                        label="Lab ID"
                                        description="Lab ID auto populated with lab location and team name"
                                    >
                                        <Input type="text" value={labId} disabled />
                                    </FormField>
                                </SpaceBetween>
                                <FormField
                                    label="Owner"
                                    description="Lab owner auto populated with current user, ownership can be updated post-creation"
                                >
                                    <Input type="text" value={owner} disabled />
                                </FormField>
                                <InputWithValidation
                                    label="Access group"
                                    type="text"
                                    description="LDAP group that can manage hosts and devices in the lab"
                                    errorText={accessGroupError}
                                    placeholder="Enter a LDAP group that can manage hosts and devices in the lab"
                                    value={accessGroup}
                                    setValue={setAccessGroup}
                                    setError={setAccessGroupError}
                                ></InputWithValidation>
                                <FormField
                                    label={
                                        <span>
                                            DOTS pool name <i>- optional</i>{' '}
                                        </span>
                                    }
                                    description="All the devices in the lab will be automatically added into this DOTS pool"
                                >
                                    <Input
                                        placeholder="Enter optional DOTS pool name as default pool for devices in the lab"
                                        value={dotsPoolName}
                                        onChange={(event) => {
                                            setDotsPoolName(event.detail.value);
                                        }}
                                    />
                                </FormField>
                                <FormField
                                    label="Device and host disconnection monitoring"
                                    description="Enable automatic monitoring of device and host disconnections. When enabled, tickets will be sent to the configured lab CTI if devices or hosts become disconnected."
                                >
                                    <RadioGroup
                                        value={monitorEnabled ? 'enabled' : 'disabled'}
                                        onChange={({ detail }) => setMonitorEnabled(detail.value === 'enabled')}
                                        items={[
                                            { value: 'enabled', label: 'Enabled' },
                                            { value: 'disabled', label: 'Disabled' },
                                        ]}
                                    />
                                </FormField>
                                <InputWithValidation
                                    label="Ticket category"
                                    type="text"
                                    description="Provide category as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketCategoryError}
                                    placeholder="Enter category as part of CTI information"
                                    value={ticketCategory}
                                    setValue={setTicketCategory}
                                    setError={setTicketCategoryError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Ticket type"
                                    type="text"
                                    description="Provide type as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketTypeError}
                                    placeholder="Enter type as part of CTI information"
                                    value={ticketType}
                                    setValue={setTicketType}
                                    setError={setTicketTypeError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Ticket item"
                                    type="text"
                                    description="Provide item as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketItemError}
                                    placeholder="Enter item as part of CTI information"
                                    value={ticketItem}
                                    setValue={setTicketItem}
                                    setError={setTicketItemError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Ticket resolver group"
                                    type="text"
                                    description="Provide resolver group as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketGroupError}
                                    placeholder="Enter resolver group as part of CTI information"
                                    value={ticketGroup}
                                    setValue={setTicketGroup}
                                    setError={setTicketGroupError}
                                ></InputWithValidation>
                            </SpaceBetween>
                        </Container>
                        <Flashbar items={errorMessage} />
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
